import { apiClient } from "@/lib/api";
import type { Changed } from "@/components/table/changedColumns";
import type { ActivityPlace } from "@/features/orders/LocationPicker";
import type { AdrLineInput } from "@/features/orders/orders.api";

// Transport → Magazijnorders (docs/api.md §9.44, docs/partner-portal.md §16 step 8): a customer's uitlevering or
// aanlevering for a warehouse that runs in Scoretrace, entered here or in the portal, with what Scoretrace made of it.

export type WarehouseOrderKind = "outbound" | "inbound";
export type WarehouseOrderTransport = "willems" | "customer";
/**
 * sending: queued or on its way · created: Scoretrace has it · dry: acceptance, nothing made · refused: with the reason ·
 * outside_wms: collected outside the stock, never for Scoretrace (03-11-2026).
 */
export type WarehouseOrderStatus = "sending" | "created" | "dry" | "refused" | "outside_wms";
export const WAREHOUSE_ORDER_STATUSES: WarehouseOrderStatus[] = ["sending", "created", "dry", "refused", "outside_wms"];

/** What is collected outside the stock, mostly emballage: a kind of pallet (Palletcontrole's), or other with its words. */
export interface OutsideStockLine {
  kind: string;
  quantity: number;
  description: string | null;
  weightKg: number | null;
}

/** A VAS code (Warehouse → VAS) as the form offers it and the order shows it. */
export interface VasChoice {
  code: string;
  description: string | null;
  descriptionEn: string | null;
  /** header: for the whole order; article: for a line; both: either. */
  appliesTo: "header" | "article" | "both" | null;
}

export interface WarehouseOrderLine {
  article: string;
  description: string | null;
  quantity: number;
  unit: string | null;
  unitPk: number | null;
  batch: string | null;
  /** Uitleveren: the pallet chosen from the stock (14-10-2026). */
  palletNumber?: string | null;
  sscc?: string | null;
  /** Uitleveren: the VAS codes for this line (16-10-2026). */
  vasCodes?: string[];
  remark: string | null;
}

/** A line as an order shows it: its VAS codes with their text. */
export type WarehouseOrderLineView = Omit<WarehouseOrderLine, "vasCodes"> & { vas?: VasChoice[] };

export interface OrderAddress {
  name: string;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string;
  countryCode: string;
}

export interface WarehouseOrder extends Changed {
  id: string;
  number: string;
  kind: WarehouseOrderKind;
  customerReference: string;
  /** Outbound: the day it leaves the warehouse; inbound: the day it arrives. */
  day: string;
  deliveryDay: string | null;
  transport: WarehouseOrderTransport;
  address: OrderAddress | null;
  remark: string | null;
  /** Uitleveren: the VAS codes for the whole order. */
  vas?: VasChoice[];
  lines: WarehouseOrderLineView[];
  /** Collected outside the stock: what goes, instead of lines (03-11-2026). */
  outsideStockLines: OutsideStockLine[] | null;
  /** When its CMR was printed last; only outside the stock, the warehouse prints the others' after picking. */
  cmrPrintedAt: string | null;
  /** Its number in Scoretrace: the consignment's (ORD-…-1) with Willems driving, otherwise its own. */
  documentNumber: string;
  status: WarehouseOrderStatus;
  error: string | null;
  scoretraceNumber: string | null;
  sentAt: string | null;
  answeredAt: string | null;
  channel: "portal" | "manual";
  /** The owner in Scoretrace; none outside the stock. */
  owner: string | null;
  createdAt: string;
  party: { id: string; code: string; name: string };
  warehouse: { code: string; name: string };
  order: { id: string; number: string } | null;
  dossier: { id: string; number: string | null } | null;
}

export const warehouseOrderKeys = {
  all: ["warehouse-orders"] as const,
  options: (partyId: string) => ["warehouse-orders", "options", partyId] as const,
  stock: (partyId: string, round: number) => ["warehouse-orders", "stock", partyId, round] as const,
};

export function listWarehouseOrders(): Promise<WarehouseOrder[]> {
  return apiClient.get<{ orders: WarehouseOrder[] }>("/api/v1/warehouse-orders").then((body) => body.orders);
}

/** Send it again; a collected order without an address takes where it goes (one entered before 19-10-2026). */
export function resendWarehouseOrder(id: string, address?: OrderAddress): Promise<WarehouseOrder> {
  return apiClient.post<{ order: WarehouseOrder }>(`/api/v1/warehouse-orders/${id}/resend`, address ? { address } : {}).then((body) => body.order);
}

/** A place chosen in the LocationPicker as an address: a place of Locaties as it is kept there, or as typed. */
export function addressOfPlace(place: ActivityPlace): OrderAddress {
  if (place.kind === "location") {
    const { name, street, houseNumber, houseNumberAddition, postalCode, city, countryCode } = place.location;
    return { name, street, houseNumber, houseNumberAddition, postalCode, city, countryCode };
  }
  const a = place.address;
  return {
    name: a.name.trim(),
    street: a.street.trim() || null,
    houseNumber: a.houseNumber.trim() || null,
    houseNumberAddition: a.houseNumberAddition.trim() || null,
    postalCode: a.postalCode.trim() || null,
    city: a.city.trim(),
    countryCode: a.countryCode.trim().toUpperCase() || "NL",
  };
}

/** Enough to send: a place of Locaties, or a typed address with a name and a city (as the API asks). */
export function placeComplete(place: ActivityPlace | null): boolean {
  if (!place) return false;
  if (place.kind === "location") return true;
  return place.address.name.trim().length >= 2 && place.address.city.trim().length > 0;
}

/** What a load is on, as an order in the portal names it; the first three have their loading metres by rule. */
export const CARRIERS = [
  "europallet",
  "block_pallet",
  "block_pallet_06",
  "block_pallet_09",
  "ibc_02",
  "ibc_04",
  "ibc",
  "ibc_06",
  "big_bag",
  "roll_container",
  "box",
  "other",
] as const;
export type Carrier = (typeof CARRIERS)[number];
/** Loading metres of one on the floor (services/api orderGoods.ts CARRIER_RULES); null: typed for the whole line. */
export const CARRIER_METERS: Record<Carrier, number | null> = {
  europallet: 0.4,
  block_pallet: 0.5,
  block_pallet_06: 0.6,
  block_pallet_09: 0.9,
  ibc_02: 0.2,
  ibc_04: 0.4,
  ibc: 0.5,
  ibc_06: 0.6,
  big_bag: 0.5,
  roll_container: null,
  box: null,
  other: null,
};

/** The other end of the transport: a place of Locaties, or an address as typed. */
export type WarehouseOrderStop = (
  | { locationId: string; address?: undefined }
  | { address: OrderAddress; locationId?: undefined }
) & {
  date: string;
  from?: string;
  to?: string;
  /** An end before the start runs to the next day only with this (P82). */
  toNextDay?: true;
  reference?: string | null;
  instruction?: string | null;
  /** Who to ask for there, and on what number (20-10-2026). */
  contactName?: string | null;
  contactPhone?: string | null;
  /** What this stop asks of the lorry, on top of what the place asks itself: a tail lift (22-10-2026). */
  requirements?: string[];
};

export interface NewWarehouseOrderInput {
  partyId: string;
  kind: WarehouseOrderKind;
  warehouse: string;
  owner?: string | null;
  customerReference: string;
  day: string;
  transport: WarehouseOrderTransport;
  stop?: WarehouseOrderStop | null;
  goods?: { count: number; carrier: Carrier; description: string; weightKg: number | null; loadingMeters?: number | null }[] | null;
  requirementIds?: string[];
  /** With Willems driving: the cubic metres and the ADR code of the transport (20-10-2026). */
  volumeM3?: number | null;
  adrCode?: string | null;
  /** The dangerous goods line by line (ADR 1.1.3.6); given, the ADR code is the first line's. */
  adrLines?: AdrLineInput[];
  address?: OrderAddress | null;
  remark?: string | null;
  vasCodes?: string[];
  lines: WarehouseOrderLine[];
  /** Collected outside the stock: these instead of lines, which stay empty. */
  outsideStockLines?: OutsideStockLine[];
}

export function createWarehouseOrder(input: NewWarehouseOrderInput): Promise<WarehouseOrder> {
  return apiClient.post<{ order: WarehouseOrder }>("/api/v1/warehouse-orders", input).then((body) => body.order);
}

/** The CMR of an order collected outside the stock, at a print location (409 NOT_OUTSIDE_STOCK, 503 PRINTING_NOT_CONFIGURED). */
export function printWarehouseOrderCmr(id: string, printLocationId: string): Promise<{ printId: string; printLocation: { code: string; name: string }; order: WarehouseOrder }> {
  return apiClient.post(`/api/v1/warehouse-orders/${id}/cmr/print`, { printLocationId });
}

export interface WarehouseOptions {
  owners: string[];
  warehouses: { code: string; name: string; scoretraceAddressPk: number }[];
  /** Every VAS code in use that can go on an order. */
  vasCodes?: VasChoice[];
}

export function warehouseOptions(partyId: string): Promise<WarehouseOptions> {
  return apiClient.get<WarehouseOptions>(`/api/v1/parties/${partyId}/warehouse-options`);
}

/** A pallet line of the customer's stock, as the portal's Voorraad has it. */
export interface StockLine {
  sscc: string | null;
  palletNumber: string | null;
  bestBefore: string | null;
  owner: { code: string; name: string | null };
  article: string;
  description: string | null;
  batch: string | null;
  quantity: number;
  unit: string | null;
  unitPk: number | null;
  status: "free" | "reserved" | "blocked";
  warehouse: { code: string; name: string | null };
}

export interface StockRequest {
  id: string;
  status: "pending" | "ok" | "failed";
  asOf: string | null;
  error: string | null;
  lines: StockLine[] | null;
}

export function customerStock(partyId: string, refresh: boolean): Promise<StockRequest> {
  return apiClient
    .get<{ request: StockRequest }>(`/api/v1/parties/${partyId}/warehouse-stock${refresh ? "?refresh=1" : ""}`)
    .then((body) => body.request);
}

export function customerStockRequest(partyId: string, id: string): Promise<StockRequest> {
  return apiClient.get<{ request: StockRequest }>(`/api/v1/parties/${partyId}/warehouse-stock/${id}`).then((body) => body.request);
}

/** The articles of a stock answer for one warehouse and owner: free quantity per article, in its unit. */
export interface StockArticle {
  article: string;
  description: string | null;
  unit: string | null;
  unitPk: number | null;
  free: number;
}

export function articlesOf(lines: StockLine[], warehouseStockCode: string | null, owner: string | null): StockArticle[] {
  const byArticle = new Map<string, StockArticle>();
  for (const line of lines) {
    if (warehouseStockCode && line.warehouse.code !== warehouseStockCode) continue;
    if (owner && line.owner.code !== owner) continue;
    const found = byArticle.get(line.article) ?? { article: line.article, description: line.description, unit: line.unit, unitPk: line.unitPk, free: 0 };
    if (line.status === "free") found.free += line.quantity;
    if (!found.description && line.description) found.description = line.description;
    if (found.unitPk === null && line.unitPk !== null) {
      found.unitPk = line.unitPk;
      found.unit = line.unit;
    }
    byArticle.set(line.article, found);
  }
  return [...byArticle.values()].sort((a, b) => (a.description ?? a.article).localeCompare(b.description ?? b.article));
}

/** A free pallet of an article, to tick for an uitlevering (14-10-2026): one stock line. */
export interface PalletChoice {
  key: string;
  sscc: string | null;
  palletNumber: string | null;
  batch: string | null;
  bestBefore: string | null;
  quantity: number;
  unit: string | null;
  unitPk: number | null;
}

/** The free pallets of each article in one warehouse and owner, the shortest best before first. */
export function palletsOf(lines: StockLine[], warehouseStockCode: string | null, owner: string | null): Map<string, PalletChoice[]> {
  const byArticle = new Map<string, PalletChoice[]>();
  lines.forEach((line, index) => {
    if (line.status !== "free" || line.quantity <= 0) return;
    if (warehouseStockCode && line.warehouse.code !== warehouseStockCode) return;
    if (owner && line.owner.code !== owner) return;
    const list = byArticle.get(line.article) ?? [];
    list.push({
      key: `${line.sscc ?? ""}|${line.palletNumber ?? ""}|${line.batch ?? ""}|${index}`,
      sscc: line.sscc,
      palletNumber: line.palletNumber,
      batch: line.batch,
      bestBefore: line.bestBefore,
      quantity: line.quantity,
      unit: line.unit,
      unitPk: line.unitPk,
    });
    byArticle.set(line.article, list);
  });
  for (const list of byArticle.values()) {
    list.sort((a, b) => (a.bestBefore ?? "9999").localeCompare(b.bestBefore ?? "9999") || (a.sscc ?? "").localeCompare(b.sscc ?? ""));
  }
  return byArticle;
}

/** The free batches of an article, with how much: to choose from, or to type another. */
export function batchesOf(pallets: PalletChoice[]): { batch: string; free: number }[] {
  const free = new Map<string, number>();
  for (const pallet of pallets) if (pallet.batch) free.set(pallet.batch, (free.get(pallet.batch) ?? 0) + pallet.quantity);
  return [...free.entries()].map(([batch, quantity]) => ({ batch, free: quantity })).sort((a, b) => a.batch.localeCompare(b.batch));
}

/**
 * The pallets to tick on a line with a batch typed or chosen (20-10-2026): only those whose batch holds what is typed,
 * and the ones already ticked. Without a batch, all of them.
 */
export function palletsOfBatch(pallets: PalletChoice[], batch: string, ticked: readonly string[]): PalletChoice[] {
  const wanted = batch.trim().toLowerCase();
  if (!wanted) return pallets;
  return pallets.filter((pallet) => ticked.includes(pallet.key) || (pallet.batch ?? "").toLowerCase().includes(wanted));
}

/** One of the customer's own articles (W11), with the units Scoretrace knows it in: the box first. */
export interface CatalogArticle {
  code: string;
  description: string;
  units: { level: "cu" | "tu" | "du"; unit: string; unitPk: number | null }[];
}

export function customerArticles(partyId: string, owner: string): Promise<CatalogArticle[]> {
  return apiClient
    .get<{ articles: CatalogArticle[] }>(`/api/v1/parties/${partyId}/warehouse-articles?${new URLSearchParams({ owner }).toString()}`)
    .then((body) => body.articles);
}
